import React, { useState, createContext, useContext } from 'react';
type Language = 'en' | 'es';
interface Translations {
  [key: string]: any;
}
const translations: Record<Language, Translations> = {
  en: {
    urgency: 'Limited to 3 moves per day — Secure your spot at least 72 hours before your move!',
    nav: {
      packages: 'Packages',
      coverage: 'Coverage',
      howItWorks: 'How It Works',
      faq: 'FAQ',
      cta: 'Choose My Package'
    },
    hero: {
      title: 'ORLANDO, YOUR MOVE HAS A RESCUE SQUAD!',
      subtitle: "Don't let the I-4 and hidden tolls burn your budget. At Move Fighters Orlando, the price you see is the price you pay. No surprises, no extra tolls, just elite service.",
      cta: 'BOOK MY RESCUE IN ORLANDO'
    },
    problem: {
      title: 'THE "HIDDEN TAX" OF MOVING IN ORLANDO',
      subtitle: 'Why is moving in Orlando so expensive with other companies?',
      points: ['They charge you by the hour while stuck on I-4 traffic.', 'Central FL tolls (Turnpike, 408, 528) get added to your final bill.', '"Travel time" from their warehouse to your house — you pay for it.', 'Summer heat? They charge "difficulty fees" on top.'],
      comparisonTable: {
        title: 'THE LOCAL SCAM vs. MOVE FIGHTERS',
        headers: ['The Local Scam', 'What Others Charge', 'Move Fighters'],
        rows: [['I-4 / 408 Traffic', 'They charge by the hour while stuck', '$0 — Fixed Price'], ['Central FL Tolls', 'Added to your final bill', 'INCLUDED'], ['"Travel Time"', 'Warehouse to your house fee', '$0 — Zero travel charges'], ['Summer Heat', '"Difficulty" surcharges', "WE ARE FIREFIGHTERS — We're ready"]]
      },
      conclusion: "In the end, your 'low cost' move ends up costing $1,500 or more.",
      extinguish: 'At Move Fighters, we are the Moving Firefighters. If you are within a 40-mile radius of Downtown Orlando, your price is unique, sacred, and guaranteed.'
    },
    packages: {
      title: 'SELECT YOUR RESCUE UNIT',
      subtitle: 'All inclusive: Truck, Pro Team, Assembly/Disassembly, TV Protection, Courtesy Boxes, and Zero Stress. (40-Mile Coverage from Orlando DT)',
      webExclusive: 'Web-exclusive pricing — these terms only apply when you buy through our website.',
      buyNow: 'BUY NOW',
      vsCompetitors: 'Vs. {price} with competitors',
      includes: 'Includes:',
      closingNotes: ['Coverage Radius: 40 miles from Downtown Orlando.', 'Advance Notice: Minimum 72-hour reservation required.', 'Process: Once you make payment, our specialists will call you to receive the exact origin and destination addresses.'],
      units: [{
        id: 'basic',
        name: 'THE STUDIO RESCUE',
        desc: 'Ideal for professionals in Downtown, Lake Nona or Studios near Disney.',
        price: 749,
        competitorPrice: '$950+',
        features: ['2 Moving Firefighters + Moving Truck', 'Bed (Frame/Box), Sofa, Dining, Mirrors & TV Protection', '4 Professional Boxes + 1 Tape incl.', 'NO EXTRA CHARGES: Diesel, Tolls & Stairs already included']
      }, {
        id: 'commander',
        name: 'THE COMMANDER',
        desc: 'The standard for apartments in Winter Garden, Kissimmee or Altamonte.',
        price: 1149,
        competitorPrice: '$1,500+',
        features: ['3 Moving Firefighters + Moving Truck', 'Full protection for 2 rooms & common areas', '8 Professional Boxes + 2 Tapes incl.', 'NO EXTRA CHARGES: Diesel, Tolls & Stairs already included']
      }, {
        id: 'captain',
        name: 'THE CAPTAIN',
        desc: 'Rescue for family homes in Windermere, Lake Mary or St. Cloud.',
        price: 1499,
        competitorPrice: '$2,100+',
        features: ["3 Elite Specialists + 26' Truck", 'Full load: rooms, garage & outdoor furniture', '11 Professional Boxes + 3 Tapes incl.', 'NO EXTRA CHARGES: Diesel, Tolls & Stairs already included']
      }, {
        id: 'battalion',
        name: 'THE BATTALION PRO',
        desc: 'For the largest residences in Central Florida. Maximum loading power.',
        price: 1899,
        competitorPrice: 'Best value in Central FL',
        features: ['4 Moving Firefighters + XL Truck', 'Maximum priority & reinforced protection for entire property', '15 Professional Boxes + 4 Tapes incl.', 'NO EXTRA CHARGES: Diesel, Tolls & Stairs already included']
      }]
    },
    coverage: {
      title: 'OPERATION ORLANDO',
      subtitle: 'The Rescue Map - Central Florida',
      description: "Are you on the map? Your price is guaranteed. If your move is outside this radius, call us for a 'Long Distance Rescue'.",
      radius: '40-Mile Rescue Radius'
    },
    howItWorks: {
      title: 'THE FIGHTER SYSTEM',
      steps: [{
        title: 'Buy Your Product',
        desc: 'Choose your home size and pay 100% today. No time wasted on phone quotes.'
      }, {
        title: 'Tell Us Where',
        desc: 'Enter origin and destination (within the 40-mile radius).'
      }, {
        title: 'Choose Your Day',
        desc: 'Select your date with at least 72 hours of anticipation.'
      }, {
        title: 'Command Confirmation',
        desc: 'Our specialists call you in minutes to coordinate building access and loading details.'
      }]
    },
    trust: {
      title: 'TOTAL CERTAINTY',
      factors: [{
        title: 'No "Day Laborers"',
        desc: 'Our team is trained to move pianos, delicate furniture, and giant TVs with rescue precision.'
      }, {
        title: 'Blind Pricing',
        desc: 'Stairs, Diesel, and Tolls for the 408 or I-4 are already in the bill. Do not take out your wallet after paying on the web.'
      }, {
        title: 'Firefighter Level Protection',
        desc: 'We use industrial blankets and shrink-wrap on every piece of furniture. If it is not protected, it does not get on the truck.'
      }, {
        title: 'No-Extra Guarantee',
        desc: "If you bought it here, we won't ask for a single dollar more on moving day."
      }]
    },
    gallery: {
      title: 'FIGHTERS IN ACTION',
      subtitle: 'Real moves. Real families. Real results.',
      captions: ['Professional packing with protective blankets', 'Our branded fleet — ready to deploy', 'Happy families, successful rescues', 'The Move Fighters handshake of trust', 'Full house rescue — furniture protection guaranteed']
    },
    stats: {
      title: 'MOVE FIGHTERS BY THE NUMBERS',
      subtitle: 'Real results from real rescues across Central Florida',
      items: [{
        value: 10500,
        suffix: '+',
        label: 'Moves Completed',
        desc: 'Successful residential rescues'
      }, {
        value: 68,
        suffix: '',
        label: 'Units in Fleet',
        desc: 'Ready to deploy any day'
      }, {
        value: 35,
        suffix: '+',
        label: 'Pro Movers',
        desc: 'Trained & uniformed Fighters'
      }, {
        value: 99,
        suffix: '%',
        label: 'Satisfaction Rate',
        desc: 'Based on customer surveys'
      }, {
        value: 40,
        suffix: 'mi',
        label: 'Coverage Radius',
        desc: 'Orlando metro & beyond'
      }, {
        value: 4.9,
        suffix: '/5',
        label: 'Star Rating',
        desc: 'Google & Yelp average'
      }],
      bars: [{
        label: 'On-Time Arrival',
        value: 98
      }, {
        label: 'Customer Satisfaction',
        value: 99
      }, {
        label: 'Zero Damage Rate',
        value: 97
      }, {
        label: 'Same-Week Availability',
        value: 85
      }]
    },
    testimonials: {
      title: 'WHAT OUR RESCUED CUSTOMERS SAY',
      subtitle: 'Real reviews from real Orlando families',
      reviews: [{
        name: 'Maria G.',
        location: 'Downtown Orlando → Lake Nona',
        rating: 5,
        text: 'Incredible service! They arrived early, wrapped everything perfectly, and finished in half the time I expected. The fixed price was exactly what I paid — no surprises. Best movers in Orlando!',
        date: 'March 2026'
      }, {
        name: 'James T.',
        location: 'Kissimmee → Winter Garden',
        rating: 5,
        text: 'After getting burned by hourly movers twice, I found Move Fighters. The Commander package was perfect for my 2-bedroom apartment. Professional, fast, and the price was locked in.',
        date: 'February 2026'
      }, {
        name: 'Sofia R.',
        location: 'UCF Area → Clermont',
        rating: 5,
        text: 'I was nervous about my piano and antique furniture. These guys treated everything like it was their own. Not a single scratch. The protective blankets they use are top quality.',
        date: 'January 2026'
      }, {
        name: 'David K.',
        location: 'Sanford → Altamonte Springs',
        rating: 5,
        text: 'The Battalion package handled our entire 4-bedroom house. 4 guys showed up at 7:55 AM sharp. Everything was done by 2 PM. Worth every penny compared to the $2,500 quotes I got elsewhere.',
        date: 'March 2026'
      }, {
        name: 'Ana L.',
        location: 'St. Cloud → Poinciana',
        rating: 5,
        text: 'Hablaban español lo cual fue genial para mi abuela. Super profesionales, rápidos y el precio fijo me dio mucha tranquilidad. 100% recomendados!',
        date: 'February 2026'
      }, {
        name: 'Robert M.',
        location: 'Winter Park → Maitland',
        rating: 5,
        text: 'Third time using Move Fighters. They never disappoint. The online booking was easy, the team was friendly, and they even assembled my bed at the new place. Five stars every time.',
        date: 'April 2026'
      }]
    },
    booking: {
      title: 'REQUEST RESCUE',
      origin: 'Origin (Full Address)',
      destination: 'Destination (Full Address)',
      date: 'Move Date',
      stairs: 'Floor/Stairs? (Info only, included in price)',
      notes: "Special Notes (e.g., 'I have a heavy marble table')",
      submit: 'CONFIRM RESCUE',
      options: {
        ground: 'Ground Floor',
        elevator: 'Elevator',
        stairs1: '1 Flight of Stairs',
        stairs2: '2+ Flights of Stairs'
      }
    },
    order: {
      yourPackage: 'Your Package',
      totalFixed: 'Fixed total — no hidden fees',
      stepDetails: 'Move Details',
      stepContact: 'Your Info',
      stepSummary: 'Review',
      moveDetails: 'Move Details',
      origin: 'Pickup Address (Origin)',
      originPlaceholder: '123 Main St, Orlando, FL 32801',
      originAccess: 'Origin — Floor / Access',
      destination: 'Delivery Address (Destination)',
      destPlaceholder: '456 Oak Ave, Kissimmee, FL 34741',
      destAccess: 'Destination — Floor / Access',
      moveDate: 'Move Date',
      minDateNote: 'Minimum 72 hours in advance required. Need a same-day move? Call us at (813) 476-8935.',
      contactInfo: 'Contact Information',
      fullName: 'Full Name',
      email: 'Email',
      phone: 'Phone Number',
      notes: 'Special Notes',
      notesPlaceholder: "e.g., 'I have a heavy marble table', 'Careful with hardwood floors'",
      stairsOptions: {
        ground: 'Ground Floor',
        elevator: 'Elevator',
        stairs1: '1 Flight of Stairs',
        stairs2: '2 Flights of Stairs',
        stairs3: '3+ Flights of Stairs'
      },
      errors: {
        required: 'This field is required.',
        outsideArea: 'This address must be within the Orlando metro area (40-mile radius). Please include the city name or FL zip code.',
        minDate: 'Move date must be at least 72 hours from now.',
        invalidEmail: 'Please enter a valid email address.',
        invalidPhone: 'Please enter a valid phone number (min 10 digits).'
      },
      areaQualifies: 'Area qualifies — within Orlando 40-mile service zone',
      next: 'Continue',
      back: 'Back',
      reviewOrder: 'Review Order',
      orderSummary: 'Order Summary',
      fromLabel: 'From',
      toLabel: 'To',
      dateLabel: 'Move Date',
      allIncluded: 'Truck, team, blankets, boxes, diesel — all included.',
      emailNotice: 'After confirming, your order details will be sent to our team and you will receive a confirmation email.',
      confirmAndPay: 'Confirm & Place Order',
      successTitle: 'Rescue Confirmed!',
      successMessage: 'Your order has been placed successfully. Our team will contact you shortly to confirm the details. Check your email for the order confirmation.',
      orderRef: 'Order Reference',
      close: 'Close'
    },
    faq: {
      title: 'FREQUENTLY ASKED QUESTIONS',
      questions: [{
        q: 'What if I move to Daytona or Tampa (more than 40 miles)?',
        a: "This package is exclusive for local moves in the Orlando metropolitan area (40-mile radius). If you are going further, contact us for a 'Long Distance' quote."
      }, {
        q: 'How do I know the price is real?',
        a: 'Because Move Fighters was born to combat scammers. The price you pay here is the only charge. There are no mandatory tips or "fees" for walking too far to the truck.'
      }, {
        q: 'Can I change the date after purchasing?',
        a: 'Sure! You have 2 free changes if you notify us 72 hours in advance. After the second change, there is a $100 logistics fee.'
      }, {
        q: 'Do you really include diesel and stairs?',
        a: 'YES. The Move Fighters concept is to eliminate the fine print. The price you pay on this website is the TOTAL.'
      }, {
        q: 'How can you guarantee the price with Orlando traffic?',
        a: "Easy: Because we are not a traditional moving company. We are a logistics system. We calculate our costs before you buy and we assume the traffic risk ourselves. If the I-4 is collapsed, that's our problem, not yours. Your price doesn't move a single dollar."
      }, {
        q: 'Do you really include the tolls?',
        a: 'YES. Whether we take the 408, the 528, or the Turnpike to reach your destination, those charges are already paid within your package. There are no surprises at the end of the day.'
      }]
    },
    footer: {
      tagline: 'Rescuing Your Home in Orlando.',
      licensed: 'Licensed & Insured — FL Mover Registration Active',
      help: 'Need immediate help?',
      callNow: 'Call Now',
      rights: 'All rights reserved.',
      services: {
        title: 'Our Services',
        items: ['Residential Moving', 'Apartment Moving', 'Furniture Assembly', 'TV & Electronics Protection', 'Packing & Unpacking', 'Same-Week Scheduling']
      },
      company: {
        title: 'Company',
        items: ['About Move Fighters', 'Our Fleet', 'Coverage Area', 'Careers', 'Contact Us']
      },
      legal: {
        title: 'Legal',
        terms: 'Terms of Service',
        privacy: 'Privacy Policy',
        cancellation: 'Cancellation Policy',
        insurance: 'Insurance & Liability'
      },
      contact: {
        title: 'Contact',
        phone: '(813) 476-8935',
        email: 'info@movefighters.com',
        hours: 'Mon–Sat: 7:00 AM – 8:00 PM',
        hoursSun: 'Sunday: 8:00 AM – 5:00 PM',
        address: 'Orlando, FL — Serving 40-mile radius'
      },
      newsletter: {
        title: 'Get Moving Tips & Deals',
        placeholder: 'Enter your email',
        button: 'Subscribe',
        note: 'No spam. Unsubscribe anytime.'
      },
      badges: ['Fixed Pricing', 'No Hidden Fees', 'Licensed & Insured', 'Orlando Local']
    }
  },
  es: {
    urgency: 'Limitado a 3 mudanzas por día — ¡Reserva tu cupo al menos 72 horas antes de tu mudanza!',
    nav: {
      packages: 'Paquetes',
      coverage: 'Cobertura',
      howItWorks: 'Cómo Funciona',
      faq: 'Preguntas',
      cta: 'Elegir Mi Paquete'
    },
    hero: {
      title: '¡ORLANDO, TU MUDANZA YA TIENE UN ESCUADRÓN DE RESCATE!',
      subtitle: 'No dejes que la I-4 y los cargos ocultos quemen tu presupuesto. En Move Fighters Orlando, el precio que ves es el precio que pagas. Sin sorpresas, sin peajes extra, solo un servicio de élite.',
      cta: 'RESERVAR MI RESCATE EN ORLANDO'
    },
    problem: {
      title: 'EL "IMPUESTO OCULTO" DE MUDARSE EN ORLANDO',
      subtitle: '¿Por qué mudarse en Orlando es tan caro con otros?',
      points: ['Te cobran por hora mientras están parados en el tráfico de la I-4.', 'Los peajes de Central FL (Turnpike, 408, 528) te los cargan al final.', 'El "Travel Time" de su bodega a tu casa — tú lo pagas.', '¿Calor extremo de verano? Te cobran cargos por "dificultad".'],
      comparisonTable: {
        title: 'EL ENGAÑO LOCAL vs. MOVE FIGHTERS',
        headers: ['El Engaño Local', 'Lo Que Te Cobran Otros', 'Move Fighters'],
        rows: [['Tráfico de la I-4 / 408', 'Te cobran por hora mientras están parados', '$0 — Precio Cerrado'], ['Peajes de Central FL', 'Te los cargan al final de la factura', 'INCLUIDOS'], ['"Travel Time"', 'Te cobran el tiempo de bodega a tu casa', '$0 — Cero cargos de viaje'], ['Calor Extremo / Verano', 'Cargos por "dificultad"', 'SOMOS BOMBEROS — Estamos listos']]
      },
      conclusion: "Al final, tu mudanza de 'bajo costo' termina costando $1,500 o más.",
      extinguish: 'En Move Fighters somos los Bomberos de la Mudanza. Si estás en un radio de 40 millas desde Downtown Orlando, tu precio es único, sagrado y garantizado.'
    },
    packages: {
      title: 'SELECCIONA TU UNIDAD DE RESCATE',
      subtitle: 'Todo incluido: Camión, Equipo Pro, Desarmado/Armado, Protección de TV, Cajas de Cortesía y Cero Estrés. (Cobertura 40 Millas desde Orlando DT)',
      webExclusive: 'Precios exclusivos web — estos términos solo aplican al comprar a través de nuestra página.',
      buyNow: 'COMPRAR AHORA',
      vsCompetitors: 'Vs. {price} en competencia',
      includes: 'Incluye:',
      closingNotes: ['Radio de Cobertura: 40 millas desde Downtown Orlando.', 'Antelación: Reserva mínima de 72 horas necesaria.', 'Proceso: Una vez realices el pago, nuestros especialistas te llamarán para recibir las direcciones exactas de origen y destino.'],
      units: [{
        id: 'basic',
        name: 'THE STUDIO RESCUE',
        desc: 'Ideal para profesionales en Downtown, Lake Nona o Studios cerca de Disney.',
        price: 749,
        competitorPrice: '$950+',
        features: ['2 Bomberos Mudadores + Camión de Mudanza', 'Protección de Cama (Frame/Box), Sofá, Comedor, Espejos y TV', '4 Cajas Profesionales + 1 Tape incl.', 'SIN CARGOS EXTRA: Diesel, Peajes y Escaleras ya incluidos']
      }, {
        id: 'commander',
        name: 'THE COMMANDER',
        desc: 'El estándar para apartamentos en Winter Garden, Kissimmee o Altamonte.',
        price: 1149,
        competitorPrice: '$1,500+',
        features: ['3 Bomberos Mudadores + Camión de Mudanza', 'Protección total de todos los muebles de 2 cuartos y áreas comunes', '8 Cajas Profesionales + 2 Tapes incl.', 'SIN CARGOS EXTRA: Diesel, Peajes y Escaleras ya incluidos']
      }, {
        id: 'captain',
        name: 'THE CAPTAIN',
        desc: 'Rescate para casas familiares en Windermere, Lake Mary o St. Cloud.',
        price: 1499,
        competitorPrice: '$2,100+',
        features: ["3 Especialistas de Élite + Camión de 26'", 'Carga completa de habitaciones, garaje y muebles de exterior', '11 Cajas Profesionales + 3 Tapes incl.', 'SIN CARGOS EXTRA: Diesel, Peajes y Escaleras ya incluidos']
      }, {
        id: 'battalion',
        name: 'THE BATTALION PRO',
        desc: 'Para las residencias más grandes de Central Florida. Máximo poder de carga.',
        price: 1899,
        competitorPrice: 'El mejor valor de Central FL',
        features: ['4 Bomberos Mudadores + Camión XL', 'Máxima prioridad y protección reforzada para toda la propiedad', '15 Cajas Profesionales + 4 Tapes incl.', 'SIN CARGOS EXTRA: Diesel, Peajes y Escaleras ya incluidos']
      }]
    },
    coverage: {
      title: 'OPERACIÓN ORLANDO',
      subtitle: 'El Mapa del Rescate - Central Florida',
      description: "¿Estás dentro del mapa? Tu precio está garantizado. Si tu mudanza es fuera de este radio, llámanos para una cotización de 'Larga Distancia'.",
      radius: 'Radio de Rescate de 40 Millas'
    },
    howItWorks: {
      title: 'EL SISTEMA FIGHTER',
      steps: [{
        title: 'Compra tu Producto',
        desc: 'Elige el tamaño de tu casa y paga el 100% hoy. ¿Por qué perder tiempo con cotizaciones por teléfono?'
      }, {
        title: 'Dime Dónde',
        desc: 'Ingresa origen y destino (dentro del radio de 40 millas).'
      }, {
        title: 'Elige tu Día',
        desc: 'Selecciona tu fecha con al menos 72h de anticipación.'
      }, {
        title: 'Confirmación de Comando',
        desc: 'Nuestros especialistas te llaman en minutos para coordinar accesos de edificios y detalles de carga.'
      }]
    },
    trust: {
      title: 'CERTEZA TOTAL',
      factors: [{
        title: 'No usamos "Day Laborers"',
        desc: 'Nuestro equipo está entrenado para mover pianos, muebles delicados y televisores gigantes con precisión de rescate.'
      }, {
        title: 'Precio Ciego',
        desc: 'Escaleras, Diesel y Peajes de la 408 o la I-4 ya están en la cuenta. No saques la billetera después de pagar en la web.'
      }, {
        title: 'Protección Nivel Bombero',
        desc: 'Usamos mantas industriales y shrink-wrap en cada mueble. Si no está protegido, no se sube al camión.'
      }, {
        title: 'Garantía No-Extra',
        desc: 'Si lo compraste aquí, no te pediremos ni un dólar más el día de la mudanza.'
      }]
    },
    gallery: {
      title: 'FIGHTERS EN ACCIÓN',
      subtitle: 'Mudanzas reales. Familias reales. Resultados reales.',
      captions: ['Empaque profesional con mantas protectoras', 'Nuestra flota con marca — lista para desplegar', 'Familias felices, rescates exitosos', 'El apretón de manos de confianza Move Fighters', 'Rescate de casa completa — protección de muebles garantizada']
    },
    stats: {
      title: 'MOVE FIGHTERS EN NÚMEROS',
      subtitle: 'Resultados reales de rescates reales en Central Florida',
      items: [{
        value: 10500,
        suffix: '+',
        label: 'Mudanzas Completadas',
        desc: 'Rescates residenciales exitosos'
      }, {
        value: 68,
        suffix: '',
        label: 'Unidades en Flota',
        desc: 'Listos para desplegar cualquier día'
      }, {
        value: 35,
        suffix: '+',
        label: 'Mudanceros Pro',
        desc: 'Fighters entrenados y uniformados'
      }, {
        value: 99,
        suffix: '%',
        label: 'Tasa de Satisfacción',
        desc: 'Basado en encuestas de clientes'
      }, {
        value: 40,
        suffix: 'mi',
        label: 'Radio de Cobertura',
        desc: 'Orlando metro y alrededores'
      }, {
        value: 4.9,
        suffix: '/5',
        label: 'Calificación',
        desc: 'Promedio Google y Yelp'
      }],
      bars: [{
        label: 'Llegada a Tiempo',
        value: 98
      }, {
        label: 'Satisfacción del Cliente',
        value: 99
      }, {
        label: 'Tasa Cero Daños',
        value: 97
      }, {
        label: 'Disponibilidad Semanal',
        value: 85
      }]
    },
    testimonials: {
      title: 'LO QUE DICEN NUESTROS CLIENTES RESCATADOS',
      subtitle: 'Reseñas reales de familias de Orlando',
      reviews: [{
        name: 'Maria G.',
        location: 'Downtown Orlando → Lake Nona',
        rating: 5,
        text: '¡Servicio increíble! Llegaron temprano, envolvieron todo perfectamente y terminaron en la mitad del tiempo que esperaba. El precio fijo fue exactamente lo que pagué — sin sorpresas. ¡Los mejores mudanceros de Orlando!',
        date: 'Marzo 2026'
      }, {
        name: 'James T.',
        location: 'Kissimmee → Winter Garden',
        rating: 5,
        text: 'Después de ser estafado por mudanceros por hora dos veces, encontré Move Fighters. El paquete Commander fue perfecto para mi apartamento de 2 habitaciones. Profesionales, rápidos y el precio estaba fijo.',
        date: 'Febrero 2026'
      }, {
        name: 'Sofia R.',
        location: 'UCF Area → Clermont',
        rating: 5,
        text: 'Estaba nerviosa por mi piano y muebles antiguos. Estos chicos trataron todo como si fuera suyo. Ni un solo rasguño. Las mantas protectoras que usan son de primera calidad.',
        date: 'Enero 2026'
      }, {
        name: 'David K.',
        location: 'Sanford → Altamonte Springs',
        rating: 5,
        text: 'El paquete Battalion manejó toda nuestra casa de 4 habitaciones. 4 chicos llegaron a las 7:55 AM en punto. Todo terminó a las 2 PM. Vale cada centavo comparado con las cotizaciones de $2,500 que recibí en otros lugares.',
        date: 'Marzo 2026'
      }, {
        name: 'Ana L.',
        location: 'St. Cloud → Poinciana',
        rating: 5,
        text: 'Hablaban español lo cual fue genial para mi abuela. Super profesionales, rápidos y el precio fijo me dio mucha tranquilidad. 100% recomendados!',
        date: 'Febrero 2026'
      }, {
        name: 'Robert M.',
        location: 'Winter Park → Maitland',
        rating: 5,
        text: 'Tercera vez usando Move Fighters. Nunca decepcionan. La reserva en línea fue fácil, el equipo fue amable y hasta armaron mi cama en la nueva casa. Cinco estrellas siempre.',
        date: 'Abril 2026'
      }]
    },
    booking: {
      title: 'SOLICITAR RESCATE',
      origin: 'Origen (Dirección completa)',
      destination: 'Destino (Dirección completa)',
      date: 'Fecha de la Mudanza',
      stairs: '¿Piso/Escaleras? (Solo info, incluido en precio)',
      notes: "Notas especiales (ej. 'Tengo un mueble de mármol pesado')",
      submit: 'CONFIRMAR RESCATE',
      options: {
        ground: 'Planta Baja',
        elevator: 'Elevador',
        stairs1: '1 Piso por Escaleras',
        stairs2: '2+ Pisos por Escaleras'
      }
    },
    order: {
      yourPackage: 'Tu Paquete',
      totalFixed: 'Total fijo — sin cargos ocultos',
      stepDetails: 'Detalles',
      stepContact: 'Tu Info',
      stepSummary: 'Revisar',
      moveDetails: 'Detalles de la Mudanza',
      origin: 'Dirección de Recogida (Origen)',
      originPlaceholder: '123 Main St, Orlando, FL 32801',
      originAccess: 'Origen — Piso / Acceso',
      destination: 'Dirección de Entrega (Destino)',
      destPlaceholder: '456 Oak Ave, Kissimmee, FL 34741',
      destAccess: 'Destino — Piso / Acceso',
      moveDate: 'Fecha de Mudanza',
      minDateNote: 'Se requiere un mínimo de 72 horas de anticipación. ¿Necesitas mudanza el mismo día? Llámanos al (813) 476-8935.',
      contactInfo: 'Información de Contacto',
      fullName: 'Nombre Completo',
      email: 'Correo Electrónico',
      phone: 'Número de Teléfono',
      notes: 'Notas Especiales',
      notesPlaceholder: "ej., 'Tengo un mueble de mármol pesado', 'Cuidado con el suelo de madera'",
      stairsOptions: {
        ground: 'Planta Baja',
        elevator: 'Elevador',
        stairs1: '1 Piso por Escaleras',
        stairs2: '2 Pisos por Escaleras',
        stairs3: '3+ Pisos por Escaleras'
      },
      errors: {
        required: 'Este campo es obligatorio.',
        outsideArea: 'Esta dirección debe estar dentro del área metropolitana de Orlando (radio de 40 millas). Incluya el nombre de la ciudad o código postal de FL.',
        minDate: 'La fecha de mudanza debe ser al menos 72 horas a partir de ahora.',
        invalidEmail: 'Por favor ingrese un correo electrónico válido.',
        invalidPhone: 'Por favor ingrese un número de teléfono válido (mín 10 dígitos).'
      },
      areaQualifies: 'Área califica — dentro de la zona de servicio de 40 millas de Orlando',
      next: 'Continuar',
      back: 'Atrás',
      reviewOrder: 'Revisar Orden',
      orderSummary: 'Resumen de la Orden',
      fromLabel: 'Desde',
      toLabel: 'Hasta',
      dateLabel: 'Fecha de Mudanza',
      allIncluded: 'Camión, equipo, mantas, cajas, diesel — todo incluido.',
      emailNotice: 'Al confirmar, los detalles de tu orden serán enviados a nuestro equipo y recibirás un correo de confirmación.',
      confirmAndPay: 'Confirmar y Ordenar',
      successTitle: '¡Rescate Confirmado!',
      successMessage: 'Tu orden ha sido procesada exitosamente. Nuestro equipo te contactará pronto para confirmar los detalles. Revisa tu correo para la confirmación.',
      orderRef: 'Referencia de Orden',
      close: 'Cerrar'
    },
    faq: {
      title: 'PREGUNTAS FRECUENTES',
      questions: [{
        q: '¿Qué pasa si me mudo a Daytona o Tampa (más de 40 millas)?',
        a: "Este paquete es exclusivo para mudanzas locales en el área metropolitana de Orlando (40 millas radio). Si vas más lejos, contáctanos para una cotización de 'Larga Distancia'."
      }, {
        q: '¿Cómo sé que el precio es real?',
        a: 'Porque Move Fighters nació para combatir a los estafadores. El precio que pagas aquí es el único cargo. No hay propinas obligatorias ni "fees" por caminar mucho hasta el camión.'
      }, {
        q: '¿Puedo cambiar la fecha después de pagar?',
        a: '¡Claro! Tienes 2 cambios gratis avisando 72 horas antes. Después del segundo cambio, hay un fee de $100 por logística.'
      }, {
        q: '¿Realmente incluyen el diesel y las escaleras?',
        a: 'SÍ. El concepto de Move Fighters es eliminar la letra pequeña. El precio que pagas en esta web es el TOTAL.'
      }, {
        q: '¿Cómo pueden garantizar el precio con el tráfico de Orlando?',
        a: 'Fácil: Porque no somos una empresa de mudanzas tradicional. Somos un sistema de logística. Calculamos nuestros costos antes de que compres y asumimos el riesgo del tráfico nosotros. Si la I-4 está colapsada, es nuestro problema, no el tuyo. Tu precio no se mueve ni un dólar.'
      }, {
        q: '¿Realmente incluyen los peajes?',
        a: 'SÍ. Ya sea que nos movamos por la 408, la 528 o el Turnpike para llegar a tu destino, esos cargos ya están pagados dentro de tu paquete. No hay sorpresas al final del día.'
      }]
    },
    footer: {
      tagline: 'Rescatando tu Hogar en Orlando.',
      licensed: 'Licenciados y Asegurados — Registro de Mudanceros FL Activo',
      help: '¿Necesitas ayuda inmediata?',
      callNow: 'Llamar Ahora',
      rights: 'Todos los derechos reservados.',
      services: {
        title: 'Nuestros Servicios',
        items: ['Mudanza Residencial', 'Mudanza de Apartamentos', 'Ensamblaje de Muebles', 'Protección de TV y Electrónicos', 'Empaque y Desempaque', 'Agenda en la Misma Semana']
      },
      company: {
        title: 'Compañía',
        items: ['Sobre Move Fighters', 'Nuestra Flota', 'Área de Cobertura', 'Carreras', 'Contáctanos']
      },
      legal: {
        title: 'Legal',
        terms: 'Términos de Servicio',
        privacy: 'Política de Privacidad',
        cancellation: 'Política de Cancelación',
        insurance: 'Seguro y Responsabilidad'
      },
      contact: {
        title: 'Contacto',
        phone: '(813) 476-8935',
        email: 'info@movefighters.com',
        hours: 'Lun–Sáb: 7:00 AM – 8:00 PM',
        hoursSun: 'Domingo: 8:00 AM – 5:00 PM',
        address: 'Orlando, FL — Servicio en radio de 40 millas'
      },
      newsletter: {
        title: 'Tips de Mudanza y Ofertas',
        placeholder: 'Ingresa tu email',
        button: 'Suscribirse',
        note: 'Sin spam. Cancela cuando quieras.'
      },
      badges: ['Precio Fijo', 'Sin Cargos Ocultos', 'Licenciados y Asegurados', 'Local de Orlando']
    }
  }
};
interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: Translations;
}
const LanguageContext = createContext<LanguageContextType | undefined>(undefined);
export const LanguageProvider: React.FC<{
  children: ReactNode;
}> = ({
  children
}) => {
  const [language, setLanguage] = useState<Language>('en');
  const value = {
    language,
    setLanguage,
    t: translations[language]
  };
  return <LanguageContext.Provider value={value} data-id="element-561">
      {children}
    </LanguageContext.Provider>;
};
export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};